import React from 'react';
import Link from 'next/link';
import { Theme, ThemeProps } from '@theme/base';
import { withTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { mq } from '@styles/breakpoints';
import { PlainLinkStyles } from '@styles/common';
import { Page, TripTypes } from '@model/common';
import { DestinationLinkBuilder, LinkItem } from '@model/iceberg';
import { Icons } from '@model/common/icons';
import { AppVariant } from '@model/config/brands';
import { displayTimes } from '@model/common/opening-times';
import { useI18NextContext } from '@components/hooks';
import { TestId } from '@components/test-ids';
import { Icon } from '@components/common/generic-icon';
import { TelLink } from '@components/common/tel-link';

/* ***************** *
 *       Types       *
 * ***************** */

export interface CallToBookProps {
  theme: Theme;
  destination: string;
  testId?: string;
  tripType: TripTypes;
  path: string;
  hideCta?: boolean;
  text?: string;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container: any = styled.div(({ theme }: ThemeProps) => ({
  alignContent: 'center',
  alignItems: 'center',
  background: theme.custom.colors.group10.lighter,
  borderRadius: 16,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  padding: theme.custom.spacing.medium * 1.5,
  [mq.small]: {
    flexDirection: 'row',
    padding: theme.custom.spacing.medium * 2
  }
}));

const InnerContainer: any = styled.div(({ theme }: ThemeProps) => ({
  textAlign: 'center',
  [mq.small]: {
    paddingLeft: theme.custom.spacing.medium * 2,
    textAlign: 'left'
  }
}));

const Image = styled.img({});

const Header: any = styled.h3(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h3 as any),
  color: theme.custom.colors.group1.dark,
  margin: 0
}));

const SubHeader: any = styled.h4(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group2.base,
  fontSize: '2.4rem',
  margin: 0,
  [mq.small]: {
    fontSize: '3.5rem'
  }
}));

const Text: any = styled.p({});

const PlainLink = styled(Link)({
  ...PlainLinkStyles,
  textDecoration: 'underline',
  textUnderlineOffset: '4px'
});

const ArrowIcon = styled(Icon)(({ theme }: ThemeProps) => ({
  color: theme.custom.colors.group2.base
}));

const OpeningTimes: any = styled.div(({ theme }: ThemeProps) => ({
  marginTop: theme.custom.spacing.xLarge
}));

const OpeningTimesTitle: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.h5 as any)
}));

const OpeningTimesTimes: any = styled.div(({ theme }: ThemeProps) => ({
  ...(theme.custom.typography.paragraph as any)
}));

/* ********************************* *
 *    CallToBook                     *
 * ********************************* */

export const ToursCallToBookComponent = ({
  theme,
  testId,
  destination,
  tripType,
  path,
  hideCta,
  text
}: CallToBookProps) => {
  const t: any = useI18NextContext();
  const isWhiteLabel = AppVariant.isWhitelabel();

  const linkBuilder: DestinationLinkBuilder = new DestinationLinkBuilder();
  const allToursLink: LinkItem = linkBuilder.getDestinationLink(path, tripType, Page.ALL_TOURS);

  let tripTypeLabel = 'call-to-book--tours-tour';
  let tripTypeLinkLabel = 'call-to-book--tours-tours';
  switch (tripType) {
    case TripTypes.PRIVATE_TOURS:
      tripTypeLabel = 'call-to-book--tours-private';
      tripTypeLinkLabel = 'call-to-book--tours-privates';
      break;
    case TripTypes.FLY_DRIVES:
      tripTypeLabel = 'call-to-book--tours-fly-drive';
      tripTypeLinkLabel = 'call-to-book--tours-fly-drives';
      break;
    case TripTypes.RIVER_CRUISES:
      tripTypeLabel = 'call-to-book--tours-river-cruise';
      tripTypeLinkLabel = 'call-to-book--tours-river-cruises';
      break;
    case TripTypes.CRUISES:
      tripTypeLabel = 'call-to-book--tours-cruise';
      tripTypeLinkLabel = 'call-to-book--tours-cruises';
      break;
    case TripTypes.MULTI_CENTRE:
      tripTypeLabel = 'call-to-book--tours-multi-centre';
      tripTypeLinkLabel = 'call-to-book--tours-multi-centres';
      break;
  }

  return (
    <Container data-testid={testId || TestId.callToBook.main} theme={theme}>
      <Image
        src={'/assets/images/contact/call-centre-new-360.jpg'}
        alt="Our Travel Specialists"
        width="180"
        height="180"
        loading={'lazy'}
      />
      <InnerContainer>
        <Header>{t('call-to-book--tours-header', { tripType: t(tripTypeLabel), destination })}</Header>
        <SubHeader>
          {t('call-to-book--tours-subheader')}
          <TelLink phoneNumber={t('common__brand_telephone')} />
        </SubHeader>
        {isWhiteLabel && (
          <OpeningTimes>
            <OpeningTimesTitle>{t('contact-details__title--opening-times')}</OpeningTimesTitle>
            <OpeningTimesTimes>{displayTimes}</OpeningTimesTimes>
          </OpeningTimes>
        )}
        {text && <Text>{text}</Text>}
        {!isWhiteLabel && !hideCta && (
          <Text>
            {t('call-to-book--tours-cta-text')}
            <PlainLink {...allToursLink}>
              {t('call-to-book--tours-link-text', { destination, tripType: t(tripTypeLinkLabel) })}
              <ArrowIcon name={Icons.ARROW_RIGHT} />
            </PlainLink>
          </Text>
        )}
      </InnerContainer>
    </Container>
  );
};

export const ToursCallToBook = withTheme(ToursCallToBookComponent);
